import { formatRand, productPlans, type BillingMode, type ProductCatalogItem, type ProductTierId } from './productPlans';

export type BriefMessage = { role: 'assistant' | 'client'; content: string };

/** Optional: paste your full forge-level master prompt (e.g. Cigar Society) via UI later or extend this string. */
export const DEFAULT_FORGE_MASTER_STUB = `## Master build constitution (replace with your full forge-level prompt)

Design and build with UIX-first luxury discipline: restraint, hierarchy, generous space, motion that feels intentional.
Prefer editorial layout over generic marketing sections. State emotional target and non-negotiables in your own extended document and paste above this stub when exporting.

---

`;

export type BuildIntakeBriefParams = {
  planId: ProductTierId;
  billingMode: BillingMode;
  messages: BriefMessage[];
  packageSummary: string;
  selectedIds: string[];
  catalogItems: ProductCatalogItem[];
  domainLine: string;
  masterPreamble?: string;
};

export function buildIntakePrompterMarkdown(params: BuildIntakeBriefParams): string {
  const plan = productPlans.find((item) => item.id === params.planId);
  const price =
    params.billingMode === 'monthly'
      ? plan?.monthlyPrice ?? null
      : plan?.buyoutPrice ?? null;

  const master = (params.masterPreamble?.trim() || DEFAULT_FORGE_MASTER_STUB).trimEnd();

  const selectedAddOns = params.catalogItems.filter(
    (item) =>
      !item.included_tiers.includes(params.planId) && params.selectedIds.includes(item.id),
  );

  const lines: string[] = [];

  lines.push(master);
  lines.push('');
  lines.push('---');
  lines.push('');
  lines.push('## PROJECT BRIEF (from DesignDotDev live consult)');
  lines.push('');
  lines.push(`- **Plan:** ${plan?.name ?? params.planId} (${params.planId})`);
  lines.push(
    `- **Billing:** ${params.billingMode}${price != null ? ` — ${formatRand(price)}${params.billingMode === 'monthly' ? '/month' : ' buyout'}` : ''}`,
  );
  lines.push(`- **Domain / hosting:** ${params.domainLine || 'Not specified in consult UI.'}`);
  lines.push('');
  lines.push('### Package summary (assistant + rules)');
  lines.push(params.packageSummary?.trim() || '_No summary yet._');
  lines.push('');
  lines.push('### Selected add-ons');
  if (selectedAddOns.length === 0) {
    lines.push('_None beyond base package._');
  } else {
    selectedAddOns.forEach((item) => {
      const m = item.monthly_price;
      const o = item.once_off_price;
      const priceHint =
        params.billingMode === 'monthly'
          ? m > 0
            ? `${formatRand(m)}/m`
            : m === 0 && o > 0
              ? `${formatRand(o)} once-off (buyout path)`
              : 'included / TBD'
          : o > 0
            ? `${formatRand(o)} once-off`
            : m > 0
              ? `${formatRand(m)}/m (monthly path)`
              : 'included / TBD';
      lines.push(`- **${item.label}** (\`${item.id}\`) — ${priceHint}`);
    });
  }
  lines.push('');
  lines.push('---');
  lines.push('');
  lines.push('## CONVERSATION TRANSCRIPT');
  lines.push('');
  if (params.messages.length === 0) {
    lines.push('_Empty._');
  } else {
    params.messages.forEach((message) => {
      const who = message.role === 'client' ? 'Client' : 'Assistant';
      lines.push(`**${who}:** ${message.content.replace(/\r\n/g, '\n')}`);
      lines.push('');
    });
  }
  lines.push('');
  lines.push('---');
  lines.push('');
  lines.push(
    '_End of pack — paste into your external prompter / generator. Ground-truth SKUs and pricing remain in DesignDotDev checkout._',
  );

  return lines.join('\n');
}

/** One block of plain text — paste into v0, Bolt, ChatGPT, etc. No fancy sections. */
export function buildSimpleHandoffPrompt(params: BuildIntakeBriefParams): string {
  const plan = productPlans.find((item) => item.id === params.planId);
  const price =
    params.billingMode === 'monthly'
      ? plan?.monthlyPrice ?? null
      : plan?.buyoutPrice ?? null;
  const priceLine =
    price != null
      ? `${formatRand(price)}${params.billingMode === 'monthly' ? ' per month (subscription)' : ' buyout'}`
      : 'pricing TBD';

  const selectedAddOns = params.catalogItems.filter(
    (item) =>
      !item.included_tiers.includes(params.planId) && params.selectedIds.includes(item.id),
  );

  const baseIncluded = params.catalogItems.filter(
    (item) => item.included_tiers.includes(params.planId) && params.selectedIds.includes(item.id),
  );

  const lines: string[] = [];

  lines.push('Build a website for this DesignDotDev client. Use everything below as scope truth.');
  lines.push('');
  lines.push('PACKAGE');
  lines.push(`- Tier: ${plan?.name ?? params.planId}`);
  lines.push(`- Billing: ${params.billingMode} — ${priceLine}`);
  lines.push(`- Domain / hosting: ${params.domainLine || 'Not specified.'}`);
  lines.push('');
  lines.push('INCLUDED IN THIS PACKAGE (base)');
  if (baseIncluded.length === 0) {
    lines.push('- (see tier defaults in DesignDotDev)');
  } else {
    baseIncluded.forEach((item) => lines.push(`- ${item.label}`));
  }
  lines.push('');
  lines.push('ADD-ONS THEY ARE BUYING');
  if (selectedAddOns.length === 0) {
    lines.push('- None beyond base.');
  } else {
    selectedAddOns.forEach((item) => lines.push(`- ${item.label}`));
  }
  lines.push('');
  lines.push('SUMMARY FROM OUR CONSULT');
  lines.push(params.packageSummary?.trim() || '(No summary yet — use chat below.)');
  lines.push('');
  lines.push('CHAT (what they said)');
  if (params.messages.length === 0) {
    lines.push('(No messages yet.)');
  } else {
    params.messages.forEach((message) => {
      const who = message.role === 'client' ? 'Customer' : 'Us';
      lines.push(`${who}: ${message.content.trim()}`);
    });
  }
  lines.push('');
  lines.push('Deliver to match this scope. If something conflicts, the commercial package + add-on list wins.');

  return lines.join('\n');
}

type StoredProductOrderBrief = {
  tier: ProductTierId;
  billing_mode: BillingMode;
  addons?: string[] | null;
  metadata?: Record<string, unknown> | null;
};

function normalizeStoredBriefMessages(raw: unknown): BriefMessage[] {
  if (!Array.isArray(raw)) return [];
  const out: BriefMessage[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const role = (entry as { role?: string }).role === 'client' ? 'client' : 'assistant';
    const content = (entry as { content?: unknown }).content;
    if (typeof content !== 'string') continue;
    out.push({ role, content });
  }
  return out;
}

/**
 * Rebuilds the same brief inputs the product page used, from `product_orders.metadata.spec`
 * (written at checkout). For admin-only copy/download of handoff text.
 */
export function intakeBriefParamsFromStoredOrder(
  order: StoredProductOrderBrief,
  catalogItems: ProductCatalogItem[],
): BuildIntakeBriefParams | null {
  const meta = order.metadata && typeof order.metadata === 'object' ? order.metadata : null;
  const spec = meta && typeof meta.spec === 'object' && meta.spec !== null ? (meta.spec as Record<string, unknown>) : null;
  if (!spec) return null;

  const domain = spec.domain;
  let domainLine = 'Not captured in consult';
  if (domain && typeof domain === 'object') {
    const d = domain as Record<string, unknown>;
    if (Boolean(d.needsRegistration)) {
      const host = typeof d.desiredDomain === 'string' ? d.desiredDomain : 'pending';
      const status = typeof d.availabilityStatus === 'string' ? d.availabilityStatus : 'unchecked';
      domainLine = `${host} (${status})`;
    } else {
      domainLine = 'Customer-owned domain';
    }
  }

  const baseIds = catalogItems.filter((item) => item.included_tiers.includes(order.tier)).map((item) => item.id);
  const addonIds = Array.isArray(order.addons) ? order.addons.filter((id): id is string => typeof id === 'string') : [];
  const selectedIds = Array.from(new Set([...baseIds, ...addonIds]));

  const summary = spec.summary;
  const packageSummary = typeof summary === 'string' ? summary : '';

  return {
    planId: order.tier,
    billingMode: order.billing_mode,
    messages: normalizeStoredBriefMessages(spec.messages),
    packageSummary,
    selectedIds,
    catalogItems,
    domainLine,
  };
}

